/* eslint-disable max-lines */
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React from 'react';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DoneOutlinedIcon from '@mui/icons-material/DoneOutlined';
import { EDVerificationStatus } from '../components/VerificationStatusChip/VerificationStatusCol';
import {
  StyledText,
  StyledTextSub,
  StyledTextSubNoColor,
} from '../../../components/StyledTypography';
import { UseMutationResult } from 'react-query';
import {
  EDApproveRejectRequest,
  EDApproveRejectResponse,
} from '../hooks/exceptionDeal';
import { useAccount } from '@azure/msal-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslate } from '@im/react-localization';
import { useTheme } from '@mui/system';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

export function RenderCellWithToolTip(
  item: Readonly<GridRenderCellParams<any, any, any>>
) {
  return (
    <Tooltip title={item.value}>
      <span className="table_cell_hover">{item.value}</span>
    </Tooltip>
  );
}

export function GetColumns(props: {
  approveReject: UseMutationResult<
    EDApproveRejectResponse,
    unknown,
    EDApproveRejectRequest,
    unknown
  >;
  businessType?: string | null;
  role: boolean;
  setCustomerSetIds: React.Dispatch<React.SetStateAction<string[]>>;
  setDealIds: React.Dispatch<React.SetStateAction<string[]>>;
  setImportreferenceIds: React.Dispatch<React.SetStateAction<string[]>>;
  setOpenAppRejDia: React.Dispatch<React.SetStateAction<boolean>>;
  setVendorNames: React.Dispatch<React.SetStateAction<string[]>>;
  setViewAffiliatedEndUSers: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}) {
  const user: string = useAccount()?.username ?? '';
  const [searchParams] = useSearchParams();
  const translate = useTranslate();
  const formatDateTime = useDateTimeFormatter();
  const theme = useTheme();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');

  const dealColumn: GridColDef = {
    field: 'dealnumber',
    headerName: translate('VendorDeals.DealNoShort'),
    renderCell: (item) => {
      return (
        <Stack>
          <Link
            className="table_cell_hover"
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
            }}
            to={`/vendor-program/exception-deal-details?businessType=${
              item.row.businessType
            }&dealNumber=${encodeURIComponent(item.value)}&importreferenceid=${
              item.row.importreferenceid
            }&dealid=${item.row.dealid}&customerset_id=${
              item.row.customerseT_ID
            }&isPurged=${item.row.isPurged}
            &countryCode=${countryCode}&companyCode=${companyCode}`}
          >
            <StyledText>{item.value}</StyledText>
          </Link>
          <StyledTextSub>{`${translate('ExceptionDeals.Version')} ${
            item.row.versionnumber
          }`}</StyledTextSub>
        </Stack>
      );
    },
    sortable: false,
    width: 200,
  };
  const actionsColumn: GridColDef[] = props.role
    ? [
        { ...dealColumn },
        {
          field: '',
          headerName: translate('Common.Actions'),
          renderCell: (item) => {
            const disable =
              item.row.status === 'Rejected - edits needed' ||
              item.row.status === 'New - edits needed' ||
              item.row.status === 'New Pending Reseller' ||
              item.row.status === 'New Pending EndUser' ||
              item.row.status === 'Deactivated';
            return (
              <Stack direction="row" spacing={0.5}>
                <Button
                  disabled={disable}
                  onClick={(event) => {
                    event.stopPropagation();
                    props.approveReject.mutate({
                      businessType: props.businessType ?? '',
                      customersetids: String(item.row.customerseT_ID),
                      dealids: item.row.dealid,
                      dealnumbers: item.row.dealnumber,
                      dealversions: item.row.versionnumber,
                      importreferenceids: item.row.importreferenceid,
                      isapproved: true,
                      notes: '',
                      servicePlanResolved: false,
                      userid: user,
                      vendornames: item.row.vendorname,
                    });
                  }}
                  variant="newOutlined"
                >
                  <DoneOutlinedIcon fontSize="small" />
                </Button>
                <Button
                  disabled={disable}
                  onClick={(event) => {
                    event.stopPropagation();
                    props.setCustomerSetIds([String(item.row.customerseT_ID)]);
                    props.setDealIds([item.row.dealid]);
                    props.setImportreferenceIds([item.row.importreferenceid]);
                    props.setOpenAppRejDia(true);
                    props.setVendorNames([item.row.vendorname]);
                  }}
                  variant="newOutlined"
                >
                  <ClearOutlinedIcon fontSize="small" />
                </Button>
              </Stack>
            );
          },
          sortable: false,
          width: 150,
        },
      ]
    : [
        {
          ...dealColumn,
        },
      ];
  const columns: GridColDef[] = [
    ...actionsColumn,
    {
      field: 'pendingreviewsubmitdate',
      headerName: translate('ExceptionDeals.TimeStamp'),
      renderCell: (item) => {
        const value = (
          <div>
            {item.value && formatDateTime(item.value, true)}
            {item.row.rejecteddate &&
              formatDateTime(item.row.rejecteddate, true)}
            {item.row.esclatedsubmitdate &&
              formatDateTime(item.row.esclatedsubmitdate, true)}
            {item.row.approvedate && formatDateTime(item.row.approvedate, true)}
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 150,
    },
    {
      field: 'status',
      headerName: translate('Common.Status'),
      renderCell: (item) => (
        <EDVerificationStatus item={item.value} role={props.role} />
      ),
      sortable: false,
      width: 200,
    },
    {
      field: 'vendorname',
      headerName: translate('Common.Vendor'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>{item.value}</StyledText>
            <StyledTextSub>{item.row.vendorprogramname}</StyledTextSub>
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 150,
    },
    {
      field: 'businessType',
      headerName: translate('ExceptionDeals.BusinessType'),
      renderCell: (item) => {
        return (
          <Tooltip title={item.value}>
            <span className="table_cell_hover">{item.value}</span>
          </Tooltip>
        );
      },
      sortable: true,
      width: 200,
    },
    {
      field: 'resellerondeal',
      headerName: translate('ExceptionDeals.ResellerOnDeal'),
      renderCell: (item) => {
        return (
          <Tooltip title={item.value}>
            <span className="table_cell_hover">{item.value}</span>
          </Tooltip>
        );
      },
      sortable: true,
      width: 200,
    },
    {
      field: 'resellerim',
      headerName: translate('ExceptionDeals.ImReseller'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>{item.value}</StyledText>
            <StyledText>{item.row.resellerimid}</StyledText>
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 200,
    },
    {
      field: 'enduserondeal',
      headerName: translate('ExceptionDeals.EndUserOnDeal'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>{item.value}</StyledText>
            <StyledText>{item.row.enduseraddress}</StyledText>
          </div>
        );
        return (
          <Stack flexDirection={'column'}>
            <Tooltip title={value}>{value}</Tooltip>
            {item.row.euaffiliationcount !== '0' &&
              item.row.euaffiliationcount !== null && (
                <StyledTextSubNoColor
                  data-testid="gds-vp-ed-list-affiliated-user-btn"
                  onClick={() =>
                    props.setViewAffiliatedEndUSers(item.row.dealid)
                  }
                  variant="link"
                >
                  {translate('VendorProgram.AffiliatedEndUsersCount', {
                    count: item.row.euaffiliationcount,
                  })}
                </StyledTextSubNoColor>
              )}
          </Stack>
        );
      },
      sortable: false,
      width: 200,
    },
    {
      field: 'enduserim',
      headerName: translate('ExceptionDeals.ImEndUser'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>{item.value}</StyledText>
            <StyledText>{item.row.enduserimid}</StyledText>
            <StyledText>{item.row.enduserimaddress}</StyledText>
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 200,
    },
    {
      field: 'deal_ingested_on',
      headerName: translate('VendorProgram.UploadDate'),
      renderCell: (item) => {
        const value = <div>{formatDateTime(item.value, true)}</div>;
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: true,
      width: 150,
    },
    {
      field: 'editornotes',
      headerName: translate('Common.Notes'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>
              {item.value}
              {item.row.reviewernotes ? ` /` : ''}
            </StyledText>
            <StyledText>{item.row.reviewernotes}</StyledText>
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 150,
    },
    {
      field: 'editorreviewer',
      headerName: translate('ExceptionDeals.EditorReviewer'),
      renderCell: (item) => {
        const value = (
          <div>
            <StyledText>
              {item.row.editor}
              {item.row.reviewer ? ` /` : ''}
            </StyledText>
            <StyledText>{item.row.reviewer}</StyledText>
          </div>
        );
        return <Tooltip title={value}>{value}</Tooltip>;
      },
      sortable: false,
      width: 200,
    },
  ];
  return columns;
}
